// src/components/hr/DepartmentManagement.tsx
import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  Plus,
  Edit,
  Trash2,
  Building,
  Users,
  Search,
  Filter,
  X,
  Shield,
} from 'lucide-react';
import {
  Department,
  JobTitle,
  DEPARTMENT_NAMES,
  JOB_TITLE_NAMES,
  getEmployeePermissions,
  getJobTitleDisplayName,
} from '../../types/newRoleSystem';

interface DepartmentInfo {
  id: Department;
  name: string;
  description: string;
  employeeCount: number;
  jobTitles: JobTitle[];
}

interface DepartmentManagementProps {
  onClose?: () => void;
}

const DepartmentManagement: React.FC<DepartmentManagementProps> = ({
  onClose,
}) => {
  const [departments, setDepartments] = useState<DepartmentInfo[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] =
    useState<Department | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingDepartment, setEditingDepartment] =
    useState<DepartmentInfo | null>(null);
  const [departmentToDelete, setDepartmentToDelete] =
    useState<DepartmentInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [newDepartmentDescription, setNewDepartmentDescription] = useState('');
  const [newDepartmentEmployeeCount, setNewDepartmentEmployeeCount] = useState(5);
  const [selectedJobTitles, setSelectedJobTitles] = useState<JobTitle[]>(
    Object.keys(JOB_TITLE_NAMES) as JobTitle[]
  );

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      // محاولة تحميل بيانات الأقسام من Firebase
      const departmentsDoc = await getDoc(
        doc(db, 'system_settings', 'departments')
      );

      if (departmentsDoc.exists()) {
        const data = departmentsDoc.data();
        if (data.departments) {
          setDepartments(data.departments);
          return;
        }
      }

      // إنشاء البيانات الافتراضية إذا لم توجد
      await initializeDepartments();
    } catch (error) {
      console.error('Error loading departments:', error);
      // إنشاء البيانات الافتراضية في حالة الخطأ
      await initializeDepartments();
    }
  };

  const initializeDepartments = async () => {
    // تحضير بيانات الإدارات
    const departmentData: DepartmentInfo[] = Object.entries(
      DEPARTMENT_NAMES
    ).map(([key, name]) => ({
      id: key as Department,
      name,
      description: getDepartmentDescription(key as Department),
      employeeCount: Math.floor(Math.random() * 20) + 5, // بيانات وهمية
      jobTitles: Object.keys(JOB_TITLE_NAMES) as JobTitle[],
    }));

    setDepartments(departmentData);

    // حفظ البيانات الافتراضية في Firebase
    try {
      await setDoc(doc(db, 'system_settings', 'departments'), {
        departments: departmentData,
        updatedAt: new Date(),
        updatedBy: 'system',
      });
    } catch (error) {
      console.error('Error saving default departments:', error);
    }
  };

  const saveDepartments = async (updatedDepartments: DepartmentInfo[]) => {
    try {
      await updateDoc(doc(db, 'system_settings', 'departments'), {
        departments: updatedDepartments,
        updatedAt: new Date(),
        updatedBy: 'system', // يمكن تحديثه لاستخدام معرف المستخدم الحالي
      });
      toast.success('تم حفظ التغييرات في قاعدة البيانات بنجاح');
    } catch (error) {
      console.error('Error saving departments:', error);
      toast.error('فشل في حفظ التغييرات في قاعدة البيانات');
    }
  };

  const getDepartmentDescription = (dept: Department): string => {
    const descriptions: Record<Department, string> = {
      IT: 'مسؤولة عن تطوير وصيانة الأنظمة التقنية والبنية التحتية للمعلومات',
      Accounting: 'تدير الحسابات المالية والتقارير المالية ومعالجة المدفوعات',
      HR: 'مسؤولة عن إدارة الموارد البشرية وتطوير الموظفين والتوظيف',
      CustomerService: 'تقدم الدعم والخدمات للعملاء والطلاب وأولياء الأمور',
      Education: 'تشرف على العملية التعليمية والمناهج وتطوير طرق التدريس',
      StudentAffairs: 'تدير شؤون الطلاب والأنشطة الطلابية والخدمات المساندة',
    };
    return descriptions[dept] || 'وصف الإدارة';
  };

  const filteredDepartments = departments.filter(
    dept =>
      dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dept.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getDepartmentPermissions = (dept: Department, jobTitle: JobTitle) => {
    const permissions = getEmployeePermissions(dept, jobTitle);
    return Object.entries(permissions)
      .filter(([, value]) => value === true)
      .map(([key]) => {
        const permissionNames: Record<string, string> = {
          canViewStudents: 'عرض الطلاب',
          canCreateStudents: 'إنشاء طلاب',
          canEditStudents: 'تعديل الطلاب',
          canDeleteStudents: 'حذف الطلاب',
          canViewUsers: 'عرض المستخدمين',
          canCreateUsers: 'إنشاء مستخدمين',
          canEditUsers: 'تعديل المستخدمين',
          canDeleteUsers: 'حذف المستخدمين',
          canViewEmployees: 'عرض الموظفين',
          canCreateEmployees: 'إنشاء موظفين',
          canEditEmployees: 'تعديل الموظفين',
          canDeleteEmployees: 'حذف الموظفين',
          canViewPayments: 'عرض المدفوعات',
          canManagePayments: 'إدارة المدفوعات',
          canViewReports: 'عرض التقارير',
          canManageSystem: 'إدارة النظام',
          canManageSchedules: 'إدارة الجداول',
        };
        return permissionNames[key] || key;
      });
  };

  // وظائف التعديل والحذف
  const handleEditDepartment = (department: DepartmentInfo) => {
    setEditingDepartment(department);
    setShowEditModal(true);
  };

  const handleDeleteDepartment = (department: DepartmentInfo) => {
    setDepartmentToDelete(department);
    setShowDeleteModal(true);
  };

  const confirmDeleteDepartment = async () => {
    if (!departmentToDelete) return;

    try {
      setLoading(true);
      const updatedDepartments = departments.filter(
        dept => dept.id !== departmentToDelete.id
      );
      setDepartments(updatedDepartments);
      await saveDepartments(updatedDepartments);
      toast.success('تم حذف الإدارة بنجاح');
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error('فشل في حذف الإدارة');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDepartmentToDelete(null);
    }
  };

  const saveEditedDepartment = async (updatedDept: DepartmentInfo) => {
    try {
      setLoading(true);
      const updatedDepartments = departments.map(dept =>
        dept.id === updatedDept.id ? updatedDept : dept
      );
      setDepartments(updatedDepartments);
      await saveDepartments(updatedDepartments);
      toast.success('تم تحديث الإدارة بنجاح');
    } catch (error) {
      console.error('Error updating department:', error);
      toast.error('فشل في تحديث الإدارة');
    } finally {
      setLoading(false);
      setShowEditModal(false);
      setEditingDepartment(null);
    }
  };

  const handleAddNewDepartment = async () => {
    if (!newDepartmentName.trim()) {
      toast.error('يرجى إدخال اسم الإدارة');
      return;
    }

    // تحقق من عدم وجود إدارة بنفس الاسم
    if (departments.find(dept => dept.name.toLowerCase() === newDepartmentName.trim().toLowerCase())) {
      toast.error('هذه الإدارة موجودة بالفعل');
      return;
    }

    try {
      setLoading(true);
      
      // إنشاء ID جديد للإدارة
      const newId = newDepartmentName.trim().toLowerCase().replace(/\s+/g, '_') as Department;
      
      const newDepartment: DepartmentInfo = {
        id: newId,
        name: newDepartmentName.trim(),
        description: newDepartmentDescription.trim() || 'وصل مختصر للإدارة',
        employeeCount: newDepartmentEmployeeCount,
        jobTitles: selectedJobTitles,
      };

      const updatedDepartments = [...departments, newDepartment];
      setDepartments(updatedDepartments);
      await saveDepartments(updatedDepartments);
      
      toast.success('تم إضافة الإدارة بنجاح');
      
      // إعادة تعيين الحقول
      setNewDepartmentName('');
      setNewDepartmentDescription('');
      setNewDepartmentEmployeeCount(5);
      setSelectedJobTitles(Object.keys(JOB_TITLE_NAMES) as JobTitle[]);
      setShowAddModal(false);
      
    } catch (error) {
      console.error('Error adding department:', error);
      toast.error('فشل في إضافة الإدارة');
    } finally {
      setLoading(false);
    }
  };

  const handleJobTitleToggle = (jobTitle: JobTitle, checked: boolean) => {
    if (checked) {
      setSelectedJobTitles(prev => [...prev, jobTitle]);
    } else {
      setSelectedJobTitles(prev => prev.filter(jt => jt !== jobTitle));
    }
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-2xl font-bold text-secondary-800'>
            إدارة الإدارات
          </h2>
          <p className='text-secondary-600 mt-1'>
            عرض وإدارة الإدارات والمسميات الوظيفية في النظام
          </p>
        </div>
        <button onClick={() => setShowAddModal(true)} className='btn-primary'>
          <Plus className='w-4 h-4 ml-2' />
          إضافة إدارة جديدة
        </button>
      </div>

      {/* Search and Filter */}
      <div className='flex items-center space-x-4 space-x-reverse'>
        <div className='relative flex-1'>
          <Search className='absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-4 h-4' />
          <input
            type='text'
            placeholder='البحث في الإدارات...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className='input-field pr-10'
          />
        </div>
        <button className='btn-secondary'>
          <Filter className='w-4 h-4 ml-2' />
          فلترة
        </button>
      </div>

      {/* Departments Grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
        {filteredDepartments.map(dept => (
          <div
            key={dept.id}
            className='bg-white rounded-lg shadow-sm border border-secondary-200 hover:shadow-md transition-shadow'
          >
            <div className='p-6'>
              <div className='flex items-start justify-between mb-4'>
                <div className='flex items-center'>
                  <div className='w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center'>
                    <Building className='w-6 h-6 text-primary-600' />
                  </div>
                  <div className='mr-3'>
                    <h3 className='text-lg font-semibold text-secondary-800'>
                      {dept.name}
                    </h3>
                    <div className='flex items-center text-sm text-secondary-500'>
                      <Users className='w-4 h-4 ml-1' />
                      {dept.employeeCount} موظف
                    </div>
                  </div>
                </div>
                <div className='flex items-center space-x-2 space-x-reverse'>
                  <button
                    onClick={() => handleEditDepartment(dept)}
                    className='text-secondary-400 hover:text-primary-600 transition-colors'
                    title='تعديل الإدارة'
                    aria-label='تعديل الإدارة'
                  >
                    <Edit className='w-4 h-4' />
                  </button>
                  <button
                    onClick={() => handleDeleteDepartment(dept)}
                    className='text-secondary-400 hover:text-red-600 transition-colors'
                    title='حذف الإدارة'
                    aria-label='حذف الإدارة'
                  >
                    <Trash2 className='w-4 h-4' />
                  </button>
                </div>
              </div>

              <p className='text-sm text-secondary-600 mb-4'>
                {dept.description}
              </p>

              {/* Job Titles */}
              <div className='space-y-3'>
                <h4 className='text-sm font-medium text-secondary-800'>
                  المسميات الوظيفية:
                </h4>
                <div className='space-y-2'>
                  {dept.jobTitles.map(jobTitle => (
                    <div
                      key={jobTitle}
                      className='flex items-center justify-between p-2 bg-secondary-50 rounded'
                    >
                      <span className='text-sm font-medium text-secondary-700'>
                        {getJobTitleDisplayName(jobTitle)}
                      </span>
                      <button
                        onClick={() => setSelectedDepartment(dept.id)}
                        className='text-xs text-primary-600 hover:text-primary-800'
                      >
                        عرض الصلاحيات
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setSelectedDepartment(dept.id)}
                className='w-full mt-4 btn-secondary text-sm'
              >
                عرض التفاصيل
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Department Details Modal */}
      {selectedDepartment && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden'>
            <div className='flex items-center justify-between p-6 border-b border-secondary-200'>
              <h3 className='text-xl font-bold text-secondary-800'>
                تفاصيل إدارة {DEPARTMENT_NAMES[selectedDepartment]}
              </h3>
              <button
                onClick={() => setSelectedDepartment(null)}
                className='text-secondary-400 hover:text-secondary-600'
                title='إغلاق'
                aria-label='إغلاق النافذة'
              >
                <X className='w-6 h-6' />
              </button>
            </div>

            <div className='p-6 max-h-[70vh] overflow-y-auto'>
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {Object.entries(JOB_TITLE_NAMES).map(
                  ([jobTitleKey, jobTitleName]) => {
                    const jobTitle = jobTitleKey as JobTitle;
                    const permissions = getDepartmentPermissions(
                      selectedDepartment,
                      jobTitle
                    );

                    return (
                      <div
                        key={jobTitle}
                        className='bg-secondary-50 rounded-lg p-4'
                      >
                        <h4 className='font-semibold text-secondary-800 mb-3 flex items-center'>
                          <Shield className='w-4 h-4 ml-2 text-primary-600' />
                          {getJobTitleDisplayName(jobTitle)}
                        </h4>

                        {permissions.length > 0 ? (
                          <div className='space-y-2'>
                            {permissions.map((permission, index) => (
                              <div key={index} className='flex items-center'>
                                <div className='w-2 h-2 bg-green-500 rounded-full ml-2' />
                                <span className='text-sm text-secondary-700'>
                                  {permission}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className='text-sm text-secondary-500'>
                            لا توجد صلاحيات محددة لهذا المسمى الوظيفي
                          </p>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            </div>

            <div className='flex justify-end p-6 border-t border-secondary-200 bg-secondary-50'>
              <button
                onClick={() => setSelectedDepartment(null)}
                className='btn-secondary'
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Department Modal */}
      {showEditModal && editingDepartment && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-md'>
            <div className='flex items-center justify-between p-6 border-b border-secondary-200'>
              <h3 className='text-xl font-bold text-secondary-800'>
                تعديل إدارة {editingDepartment.name}
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingDepartment(null);
                }}
                className='text-secondary-400 hover:text-secondary-600'
                title='إغلاق'
                aria-label='إغلاق النافذة'
              >
                <X className='w-6 h-6' />
              </button>
            </div>

            <div className='p-6'>
              <div className='space-y-4'>
                <div>
                  <label className='block text-sm font-medium text-secondary-700 mb-2'>
                    اسم الإدارة
                  </label>
                  <input
                    type='text'
                    value={editingDepartment.name}
                    disabled={loading}
                    onChange={e =>
                      setEditingDepartment({
                        ...editingDepartment,
                        name: e.target.value,
                      })
                    }
                    className='input-field disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500'
                    placeholder='أدخل اسم الإدارة'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-secondary-700 mb-2'>
                    وصف الإدارة
                  </label>
                  <textarea
                    value={editingDepartment.description}
                    disabled={loading}
                    onChange={e =>
                      setEditingDepartment({
                        ...editingDepartment,
                        description: e.target.value,
                      })
                    }
                    className='input-field disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500'
                    rows={3}
                    placeholder='أدخل وصف مختصر للإدارة'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-secondary-700 mb-2'>
                    عدد الموظفين
                  </label>
                  <input
                    type='number'
                    value={editingDepartment.employeeCount}
                    disabled={loading}
                    onChange={e =>
                      setEditingDepartment({
                        ...editingDepartment,
                        employeeCount: parseInt(e.target.value) || 0,
                      })
                    }
                    className='input-field disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500'
                    placeholder='عدد الموظفين'
                    min='0'
                  />
                </div>
              </div>
            </div>

            <div className='flex items-center justify-between p-6 border-t border-secondary-200 bg-secondary-50'>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingDepartment(null);
                }}
                className='btn-secondary'
                disabled={loading}
              >
                إلغاء
              </button>
              <button
                onClick={() => saveEditedDepartment(editingDepartment)}
                className='btn-primary flex items-center'
                disabled={loading || !editingDepartment.name.trim()}
              >
                {loading ? (
                  <>
                    <div className='animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent ml-2'></div>
                    جار الحفظ...
                  </>
                ) : (
                  <>
                    <Edit className='w-4 h-4 ml-2' />
                    حفظ التعديلات
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && departmentToDelete && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-md'>
            <div className='flex items-center justify-between p-6 border-b border-secondary-200'>
              <h3 className='text-xl font-bold text-red-600'>تأكيد الحذف</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDepartmentToDelete(null);
                }}
                className='text-secondary-400 hover:text-secondary-600'
                title='إغلاق'
                aria-label='إغلاق النافذة'
              >
                <X className='w-6 h-6' />
              </button>
            </div>

            <div className='p-6'>
              <div className='flex items-center gap-4 mb-4'>
                <div className='w-12 h-12 bg-red-100 rounded-full flex items-center justify-center'>
                  <Trash2 className='w-6 h-6 text-red-600' />
                </div>
                <div>
                  <h4 className='text-lg font-semibold text-secondary-800'>
                    حذف إدارة {departmentToDelete.name}
                  </h4>
                  <p className='text-secondary-600'>
                    هل أنت متأكد من حذف هذه الإدارة؟
                  </p>
                </div>
              </div>

              <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
                <p className='text-red-800 text-sm'>
                  <strong>تحذير:</strong> سيتم حذف جميع البيانات المرتبطة بهذه
                  الإدارة ولن يمكن التراجع عن هذا الإجراء.
                </p>
                <p className='text-red-700 text-sm mt-2'>
                  عدد الموظفين المتأثرين: {departmentToDelete.employeeCount}
                </p>
              </div>
            </div>

            <div className='flex items-center justify-between p-6 border-t border-secondary-200 bg-secondary-50'>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDepartmentToDelete(null);
                }}
                className='btn-secondary'
                disabled={loading}
              >
                إلغاء
              </button>
              <button
                onClick={confirmDeleteDepartment}
                className='bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors'
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className='animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent ml-2'></div>
                    جار الحذف...
                  </>
                ) : (
                  <>
                    <Trash2 className='w-4 h-4 ml-2' />
                    حذف الإدارة
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Department Modal */}
      {showAddModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-md'>
            <div className='flex items-center justify-between p-6 border-b border-secondary-200'>
              <h3 className='text-xl font-bold text-secondary-800'>
                إضافة إدارة جديدة
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className='text-secondary-400 hover:text-secondary-600'
                title='إغلاق'
                aria-label='إغلاق النافذة'
              >
                <X className='w-6 h-6' />
              </button>
            </div>

            <div className='p-6'>
              <div className='space-y-4'>
                <div>
                  <label className='block text-sm font-medium text-secondary-700 mb-2'>
                    اسم الإدارة
                  </label>
                  <input
                    type='text'
                    value={newDepartmentName}
                    disabled={loading}
                    onChange={e => setNewDepartmentName(e.target.value)}
                    className='input-field disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500'
                    placeholder='أدخل اسم الإدارة'
                    autoFocus
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-secondary-700 mb-2'>
                    وصف الإدارة
                  </label>
                  <textarea
                    value={newDepartmentDescription}
                    disabled={loading}
                    onChange={e => setNewDepartmentDescription(e.target.value)}
                    className='input-field disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500'
                    rows={3}
                    placeholder='أدخل وصف مختصر للإدارة'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-secondary-700 mb-2'>
                    عدد الموظفين المتوقع
                  </label>
                  <input
                    type='number'
                    value={newDepartmentEmployeeCount}
                    disabled={loading}
                    onChange={e => setNewDepartmentEmployeeCount(parseInt(e.target.value) || 0)}
                    className='input-field disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-500'
                    placeholder='عدد الموظفين'
                    min='0'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-secondary-700 mb-2'>
                    المسميات الوظيفية المتاحة
                  </label>
                  <div className='space-y-2 max-h-32 overflow-y-auto border border-secondary-200 rounded-lg p-3'>
                    {Object.entries(JOB_TITLE_NAMES).map(([key, name]) => {
                      const jobTitle = key as JobTitle;
                      return (
                        <label key={key} className='flex items-center'>
                          <input
                            type='checkbox'
                            checked={selectedJobTitles.includes(jobTitle)}
                            disabled={loading}
                            onChange={e => handleJobTitleToggle(jobTitle, e.target.checked)}
                            className='rounded border-secondary-300 text-primary-600 disabled:cursor-not-allowed disabled:opacity-60'
                          />
                          <span className='mr-2 text-sm text-secondary-700'>
                            {name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <p className='text-xs text-secondary-500 mt-1'>
                    اختر المسميات الوظيفية المطلوبة في هذه الإدارة
                  </p>
                </div>
              </div>
            </div>

            <div className='flex items-center justify-between p-6 border-t border-secondary-200 bg-secondary-50'>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewDepartmentName('');
                  setNewDepartmentDescription('');
                  setNewDepartmentEmployeeCount(5);
                  setSelectedJobTitles(Object.keys(JOB_TITLE_NAMES) as JobTitle[]);
                }}
                className='btn-secondary'
                disabled={loading}
              >
                إلغاء
              </button>
              <button
                onClick={handleAddNewDepartment}
                className='btn-primary flex items-center'
                disabled={loading || !newDepartmentName.trim() || selectedJobTitles.length === 0}
              >
                {loading ? (
                  <>
                    <div className='animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent ml-2'></div>
                    جار الإضافة...
                  </>
                ) : (
                  <>
                    <Plus className='w-4 h-4 ml-2' />
                    إضافة الإدارة
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagement;
